<template>
  <div class="media-viewer">
    <!-- Header with title/description/icon when showHeader is true -->
    <div v-if="showHeader && fileAsset" class="header mb-3">
      <div class="asset-type d-flex align-items-center mb-2">
        <i :class="assetIcon" class="me-2"></i>
        <h6 class="mb-0">{{ assetLabel }}</h6>
      </div>
      <h4 v-if="fileAsset.title" class="title">{{ fileAsset.title }}</h4>
      <p v-if="fileAsset.description" class="description text-muted">{{ fileAsset.description }}</p>
    </div>

    <!-- PDF Content -->
    <PdfViewer v-if="contentType === 'pdf'" :file-asset="fileAsset" :file-object="fileObject" />

    <!-- Image Content -->
    <ImageViewer
      v-else-if="contentType === 'image'"
      :file-asset="fileAsset"
      :file-object="fileObject"
      :context="context"
      :show-controls="showControls"
    />

    <!-- Unknown/Unsupported Content -->
    <div v-else class="fallback-viewer text-center p-5">
      <div class="fallback-icon mb-3">
        <i class="bi bi-file-earmark text-muted" style="font-size: 4rem"></i>
      </div>
      <h5 class="text-muted mb-3">Preview Unavailable</h5>
      <p class="text-muted mb-4">
        {{ unsupportedMessage }}
      </p>
      <button v-if="downloadUrl" @click="downloadFile" class="btn btn-primary">
        <i class="bi bi-download me-2"></i>
        Download File
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import Vue from 'vue'
import PdfViewer from '@components/PdfViewer.vue'
import ImageViewer from '@components/ImageViewer.vue'
import { reportError } from '@lib/errorHandler'
import { type ViewContext } from '@lib/imageScaling'

interface FileAsset {
  content_type?: string
  file_url?: string
  remote_file_url?: string
  title?: string
  [key: string]: unknown
}

// Content type detection helpers
const PDF_TYPES = ['application/pdf']
const IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/tiff',
]

// File extension mappings for fallback detection
const EXTENSION_MAP: Record<string, string> = {
  // PDFs
  pdf: 'pdf',
  // Images
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  bmp: 'image',
  tiff: 'image',
  tif: 'image',
}

export default defineComponent({
  name: 'MediaViewer',

  components: {
    PdfViewer,
    ImageViewer,
  },

  props: {
    fileAsset: {
      type: Object as PropType<FileAsset>,
      required: false,
      default: null,
    },
    fileObject: {
      type: File as PropType<File>,
      required: false,
      default: null,
    },
    context: {
      type: String as PropType<ViewContext>,
      default: 'embedded',
    },
    showControls: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    contentType(): string {
      // Try to detect content type from fileAsset (backend detection)
      if (this.fileAsset?.content_type) {
        return this.detectContentTypeFromMime(this.fileAsset.content_type)
      }

      // Try to detect content type from fileObject (client-side detection)
      if (this.fileObject) {
        // First try MIME type
        if (this.fileObject.type) {
          return this.detectContentTypeFromMime(this.fileObject.type)
        }

        // Fallback to file extension
        return this.detectContentTypeFromFileName(this.fileObject.name)
      }

      return 'unknown'
    },

    downloadUrl(): string | null {
      // Handle file asset from API
      if (this.fileAsset) {
        // Use remote_file_url if available (external URLs)
        if (this.fileAsset.remote_file_url) {
          return this.fileAsset.remote_file_url
        }

        // Use file_url with base URL for internal files
        if (this.fileAsset.file_url) {
          const baseUrl = process.env.VUE_APP_UE_APP_URL || ''
          return `${baseUrl}${this.fileAsset.file_url}`
        }
      }

      // For file objects, we can create a blob URL for download
      if (this.fileObject) {
        try {
          return URL.createObjectURL(this.fileObject)
        } catch (error) {
          reportError(error as Error, 'createObjectURL', 'MediaViewer')
          return null
        }
      }

      return null
    },

    fileName(): string {
      if (this.fileAsset?.title) {
        return this.fileAsset.title
      }
      if (this.fileObject?.name) {
        return this.fileObject.name
      }
      return 'file'
    },

    unsupportedMessage(): string {
      if (this.fileObject?.type) {
        return `This file type (${this.fileObject.type}) cannot be previewed.`
      }
      if (this.fileAsset?.content_type) {
        return `This file type (${this.fileAsset.content_type}) cannot be previewed.`
      }
      return 'This file type cannot be previewed.'
    },

    assetIcon(): string {
      const contentType = this.fileAsset?.content_type || this.contentType
      if (contentType === 'image') return 'bi bi-image'
      if (contentType === 'pdf') return 'bi bi-file-richtext-fill'
      return 'bi bi-file-earmark' // fallback for unknown types
    },

    assetLabel(): string {
      const contentType = this.fileAsset?.content_type || this.contentType
      if (contentType === 'pdf') return 'PDF Asset'
      if (contentType === 'image') return 'Media Asset'
      return 'File Asset' // fallback for unknown types
    },
  },

  methods: {
    detectContentTypeFromMime(mimeType: string): string {
      const mime = mimeType.toLowerCase()

      // Handle simplified content types from backend
      if (mime === 'pdf') {
        return 'pdf'
      }
      if (mime === 'image') {
        return 'image'
      }

      // Handle full MIME types
      if (PDF_TYPES.includes(mime)) {
        return 'pdf'
      }
      if (IMAGE_TYPES.includes(mime)) {
        return 'image'
      }

      // Check if it starts with common type prefixes
      if (mime.startsWith('image/')) {
        return 'image'
      }

      return 'unknown'
    },

    detectContentTypeFromFileName(fileName: string): string {
      if (!fileName) {
        return 'unknown'
      }

      const lastDot = fileName.lastIndexOf('.')
      if (lastDot === -1) {
        return 'unknown'
      }

      const extension = fileName.substring(lastDot + 1).toLowerCase()
      return EXTENSION_MAP[extension] || 'unknown'
    },

    downloadFile() {
      if (!this.downloadUrl) {
        this.$toast.error('Unable to download file')
        return
      }

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = this.downloadUrl
      link.download = this.fileName
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up blob URL if we created one for fileObject
      if (this.fileObject && this.downloadUrl.startsWith('blob:')) {
        // Delay cleanup to ensure download starts
        setTimeout(() => {
          URL.revokeObjectURL(this.downloadUrl as string)
        }, 100)
      }
    },
  },

  // Error boundary to catch and handle errors from child components
  errorCaptured(error: Error, instance: Vue, info: string) {
    reportError(error, info, 'MediaViewer')

    // Show user-friendly error message
    this.$toast.error('Unable to display media content')

    // Prevent the error from propagating
    return false
  },
})
</script>

<style lang="sass" scoped>
.media-viewer
  width: 100%
  height: 100%
  display: flex
  flex-direction: column
  position: relative

  .video-placeholder,
  .fallback-viewer
    background-color: var(--ue-gray-container-bg)
    border-radius: 0.5rem
    max-width: 600px
    margin: 0 auto
    width: 100%

    .placeholder-icon,
    .fallback-icon
      opacity: 0.5

    h5
      font-weight: 500
      color: var(--ue-gray-container-color)

    p
      color: var(--ue-gray-container-color)
      font-size: 0.95rem

  // Header styles for title/description/icon
  .header
    padding: 1rem
    background: white
    border-radius: 0.5rem
    margin-bottom: 1rem

    .asset-type
      display: flex
      align-items: center
      margin-bottom: 0.5rem

      i
        font-size: 1.25rem
        color: var(--primary)

      h6
        color: var(--primary)
        font-weight: 600
        text-transform: uppercase
        font-size: 0.875rem
        letter-spacing: 0.5px

    .title
      font-size: 1.5rem
      font-weight: 600
      margin-bottom: 0.5rem

    .description
      color: rgba(108, 116, 125, 1)
      font-weight: 400
      font-size: 0.875em
      margin-bottom: 0
</style>
